import { useMemo } from 'react'

import { useAuth } from '../../auth/authContext'
import { ApiClient, ApiError, OfflineError } from '../../data/api'
import { placesFrom, type Place } from '../../lib/geocode'

/** Sucht Orte zu einem Suchbegriff. */
export type SearchPlaces = (query: string, signal?: AbortSignal) => Promise<Place[]>

/**
 * Der Zugang zur Adresssuche, an die laufende Anmeldung gebunden.
 *
 * Ein eigener Client und nicht der des Abgleichs - dieselbe Überlegung wie bei
 * useFetchRoute in routeApi.ts: der steckt in der Engine im RoadbookProvider
 * und hat dort nichts nach draussen zu geben. ApiClient hält ausser der
 * Tokenquelle keinen Zustand, ein zweiter kostet also nichts.
 *
 * Geprüft wird hier und nicht erst in der Oberfläche: was aus dem Netz kommt,
 * ist eine fremde Behauptung, und keine Ansicht soll sich darauf verlassen
 * müssen, dass jemand anderes vor ihr geprüft hat.
 */
export function useSearchPlaces(): SearchPlaces {
  const { getToken } = useAuth()
  return useMemo(() => {
    const client = new ApiClient(getToken)
    return async (query, signal) => placesFrom((await client.searchPlaces(query, signal)).results)
  }, [getToken])
}

/**
 * Der Fehler in einem Satz, den man dem Nutzer zeigen kann.
 *
 * Dieselbe Aufteilung wie bei routeErrorText in stageRouting.ts: die Codes
 * stammen aus der Zusage des Servers, alles Unbekannte bekommt eine allgemeine
 * Meldung samt Statuscode. Erfundene Deutungen sind schlimmer als ein
 * zugegebenes „unbekannt“.
 */
export function geocodeErrorText(error: unknown): string {
  if (error instanceof OfflineError) {
    return 'Kein Netz. Die Adresssuche geht nur mit Verbindung — die Koordinate lässt sich aber von Hand eintragen.'
  }
  if (error instanceof ApiError) {
    if (error.isAuthProblem) return 'Die Anmeldung ist abgelaufen. Neu anmelden und noch einmal.'
    if (error.code === 'upstream_unavailable') {
      return 'Der Adressdienst antwortet nicht. Später noch einmal versuchen.'
    }
    if (error.code === 'bad_request') {
      return 'Mit diesem Suchbegriff kann der Dienst nichts anfangen.'
    }
    return `Der Server antwortete mit ${String(error.status)} (${error.code}).`
  }
  return 'Die Suche ist fehlgeschlagen.'
}
